import { Heading, VStack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useFormTemplates } from "src/components/Providers/FormTemplateProvider";
import { listFormIdsV2 } from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as breadcrumb from "src/services/breadcrumb";
import { throwIfFormServiceError } from "src/services/error";
import * as GQL from "src/types/graphql";
import { resolveSummaryFormIds } from "../components/summaryFormIds";
import { GET_APPLICABLE_VERIFICATIONS_SUMMARIES } from "../graphql/queries";
import type { BulkTargets } from "../types";
import { BulkVerificationFormAsync } from "./BulkVerificationFormAsync";

export interface BulkVerificationsAsyncState {
  formTemplateId: uuid;
  bulkTargets: BulkTargets;
  selectionSize: number;
}

export const BulkVerificationsAsync: React.FC = () => {
  const location = useLocation();
  const organization = useOrganization();
  const [searchParams] = useSearchParams();
  const { selectedNavFormTemplate } = useFormTemplates();

  const {
    formTemplateId = "",
    bulkTargets,
    selectionSize = 0,
  } = (location.state ?? {}) as Partial<BulkVerificationsAsyncState>;
  const isBadState = !formTemplateId || !bulkTargets;
  const isExclusionMode = bulkTargets != null && "cacheKey" in bulkTargets;

  const inclusionFormIds = useMemo(
    () =>
      bulkTargets != null && "inclusion" in bulkTargets
        ? resolveSummaryFormIds(bulkTargets)
        : undefined,
    [bulkTargets]
  );

  const queryParams = useMemo(
    () => ({
      formTemplateId: bulkTargets?.formTemplateId ?? "",
      ...Object.fromEntries(searchParams.entries()),
    }),
    [bulkTargets?.formTemplateId, searchParams]
  );

  const exclusionQuery = useQuery({
    queryKey: ["bulkVerifySummaryFormIds", bulkTargets, queryParams],
    queryFn: async () => {
      if (!bulkTargets || !("cacheKey" in bulkTargets)) {
        return [] as uuid[];
      }
      const response = throwIfFormServiceError(
        await listFormIdsV2({ query: queryParams })
      );
      return resolveSummaryFormIds(bulkTargets, response.data.items ?? []);
    },
    enabled: !isBadState && isExclusionMode,
  });

  const summaryFormIds = inclusionFormIds ?? exclusionQuery.data ?? [];

  const { remoteData: verificationsSummariesData } = useRemoteDataQuery<
    GQL.GetApplicableVerificationsSummaries,
    GQL.GetApplicableVerificationsSummariesVariables
  >(GET_APPLICABLE_VERIFICATIONS_SUMMARIES, {
    variables: {
      form_template_id: formTemplateId,
      form_ids: summaryFormIds,
    },
    skip:
      isBadState ||
      (isExclusionMode && exclusionQuery.isLoading) ||
      !summaryFormIds.length,
    fetchPolicy: "no-cache",
  });

  return (
    <VStack alignItems="start" minHeight="100%">
      <Breadcrumb
        mb={4}
        items={breadcrumb.form.getBreadcrumbsForBulkVerifications(
          organization,
          selectedNavFormTemplate,
          searchParams.toString()
        )}
      />

      <Heading as="h1" size="lg" pb={8}>
        Verify {selectionSize} form
        {selectionSize !== 1 ? "s" : ""}
      </Heading>

      {isBadState || (!exclusionQuery.isLoading && !summaryFormIds.length) ? (
        <>No forms selected.</>
      ) : isExclusionMode && exclusionQuery.isLoading ? (
        <Loading />
      ) : exclusionQuery.isError ? (
        <GenericError />
      ) : (
        <GQLRemoteDataView
          remoteData={verificationsSummariesData}
          error={() => <GenericError />}
          loading={<Loading />}
        >
          {(data) => (
            <BulkVerificationFormAsync
              bulkTargets={bulkTargets}
              formIds={summaryFormIds}
              selectionSize={selectionSize}
              applicableVerificationsSummaries={data}
            />
          )}
        </GQLRemoteDataView>
      )}
    </VStack>
  );
};
